/**
 * Activation (org owner, "path B" step 2): "Secure your admin account" —
 * password, then two-step verification. Design: ob-entry.jsx ScreenSecure
 * (password card + MFA card side by side in `.two`).
 *
 * 2FA enrolment needs a session, and the session needs the account, so the
 * two cards run in sequence:
 *   1. password valid → Continue → accept → sign in (the MFA card waits until
 *      then, and the password card turns into "Password set");
 *   2. the MFA card offers authenticator app / secondary email / later. Either
 *      method ends in recovery codes (onSecured); "later" goes straight on
 *      (onSkipMfa) with no recovery codes.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, LifeBuoy } from "lucide-react";
import {
  Btn,
  Field,
  Ic,
  MfaSetup,
  ObMain,
  ObShell,
  PasswordCard,
  passwordReady,
  pwNameParts,
  type InvitationErrorBody,
  type InvitationValidation,
} from "../kit";
import { useShellStore } from "../../../store";
import { AccountErrorNote, InviteeTopBar, PasswordDoneCard, orgDisplayName } from "./common";
import { createAccountAndSignIn, type CreateAccountOutcome } from "./session";

type Stage = "password" | "mfa";

export interface SecureAdminScreenProps {
  token: string;
  invitation: InvitationValidation;
  /** The link stopped working mid-flow (expired / revoked / accepted / invalid). */
  onLinkProblem: (code: string, body: InvitationErrorBody | null) => void;
  /** Account created but no session came back — send them to sign in. */
  onSignInNeeded: () => void;
  /** 2FA confirmed; recovery codes are next. */
  onSecured: (recoveryCodes: string[]) => void;
  /** "Set it up later": the account exists and is signed in; skip 2FA for now. */
  onSkipMfa: () => void;
}

export function SecureAdminScreen({ token, invitation, onLinkProblem, onSignInNeeded, onSecured, onSkipMfa }: SecureAdminScreenProps) {
  const { t } = useTranslation();
  const setUser = useShellStore((s) => s.setUser);
  const org = orgDisplayName(invitation.organization, invitation.organization_name);
  const minLength = invitation.password_policy?.min_length ?? 12;

  const [stage, setStage] = useState<Stage>("password");
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const needsName = !(invitation.name ?? "").trim();
  const [name, setName] = useState("");
  const [pwErrors, setPwErrors] = useState<string[] | null>(null);
  const [acctErr, setAcctErr] = useState<Extract<CreateAccountOutcome, { status: "error" }> | null>(null);

  const personName = invitation.name || name;
  const nameParts = pwNameParts(personName, invitation.login_email, invitation.organization?.name);

  const createAccount = async () => {
    setPwErrors(null);
    setAcctErr(null);
    const outcome = await createAccountAndSignIn({
      token,
      password: pw,
      name: needsName ? name : undefined,
      loginEmail: invitation.login_email,
      setUser,
    });
    switch (outcome.status) {
      case "signed-in":
        setStage("mfa");
        return;
      case "created":
        onSignInNeeded();
        return;
      case "password":
        setPwErrors(outcome.errors.length ? outcome.errors : [t("onboarding.invitee.err.password", "Choose a different password.")]);
        return;
      case "link":
        onLinkProblem(outcome.code, outcome.body);
        return;
      default:
        setAcctErr(outcome);
    }
  };

  const next = async () => {
    if (stage !== "password") return;
    setBusy(true);
    try {
      await createAccount();
    } finally {
      setBusy(false);
    }
  };

  const canContinue = stage === "password" && passwordReady(pw, pw2, nameParts, minLength) && (!needsName || !!name.trim());

  return (
    <ObShell>
      <InviteeTopBar orgName={org} />
      <ObMain width={1040}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20, padding: "0 4px" }} data-screen-label="Secure admin account">
          <p className="ob-eye">{t("onboarding.invitee.secure.eyebrow", "Secure your admin account")}</p>
          <h1 className="ob-h1">
            {org
              ? t("onboarding.invitee.secure.title", { defaultValue: "Protect the keys to {{org}}.", org })
              : t("onboarding.invitee.secure.titleNoOrg", "Protect your workspace.")}
          </h1>
          <p className="ob-p" style={{ maxWidth: 640 }}>
            {t(
              "onboarding.invitee.secure.body",
              "You're the owner of this workspace. Set a strong password, then turn on two-step verification with an authenticator app or your personal email. You can also set it up later, but we strongly recommend doing it now.",
            )}
          </p>
        </div>

        {acctErr && (
          <div style={{ marginBottom: 16 }}>
            <AccountErrorNote outcome={acctErr} activation />
          </div>
        )}

        <div className="two">
          {stage === "password" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {needsName && (
                <section className="ob-glass" style={{ padding: "clamp(20px,3cqi,28px)" }}>
                  <Field label={t("onboarding.invitee.yourName", "Your full name")}>
                    <input className="ob-in" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                  </Field>
                </section>
              )}
              <PasswordCard
                value={pw}
                onChange={(v) => {
                  setPw(v);
                  setPwErrors(null);
                }}
                confirm={pw2}
                onConfirmChange={setPw2}
                email={invitation.login_email}
                nameParts={nameParts}
                minLength={minLength}
                serverErrors={pwErrors}
                onSubmit={() => {
                  if (canContinue && !busy) void next();
                }}
              />
            </div>
          ) : (
            <PasswordDoneCard email={invitation.login_email} />
          )}
          <MfaSetup
            step={2}
            secondaryEmailMasked={invitation.secondary_email_masked ?? null}
            onConfirmed={onSecured}
            onLater={onSkipMfa}
            laterHint={t(
              "onboarding.invitee.secure.laterHint",
              "Skip for now and turn it on later in Settings → Security. Until then, your password alone protects the workspace.",
            )}
            waiting={
              stage === "password"
                ? t("onboarding.invitee.secure.mfaWaiting", "Set your password first. You can choose how to verify once your account exists.")
                : undefined
            }
          />
        </div>

        <div
          className="ob-glass"
          style={{ marginTop: 20, padding: "16px 20px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 10, flex: "1 1 280px", minWidth: 0 }}>
            <Ic icon={LifeBuoy} size={22} color="var(--bl)" />
            <span className="ob-sm" style={{ color: "var(--tx)" }}>
              {t(
                "onboarding.invitee.secure.recoveryHint",
                "Once two-step verification is on, you'll get 10 recovery codes. They're the only way back in if you lose access to your phone or personal email.",
              )}
            </span>
          </span>
          {stage === "password" && (
            <Btn size="l" disabled={!canContinue} busy={busy} onClick={() => void next()} iconEnd={ArrowRight} flipEnd>
              {t("onboarding.kit.continue", "Continue")}
            </Btn>
          )}
        </div>
      </ObMain>
    </ObShell>
  );
}
